// Web Push côté navigateur. L'opt-in est explicite et par navigateur :
// Notification.requestPermission() DOIT rester dans le gestionnaire de
// clic (geste utilisateur requis par Chrome/Safari), donc subscribePush
// est appelé depuis l'interrupteur, jamais au montage.
import { apiClient } from '../api/client.js';

export function pushSupported(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    'serviceWorker' in navigator &&
    typeof window !== 'undefined' &&
    'PushManager' in window &&
    'Notification' in window
  );
}

export function pushPermission(): NotificationPermission | null {
  return pushSupported() ? Notification.permission : null;
}

export async function getSubscription(): Promise<PushSubscription | null> {
  if (!pushSupported()) return null;
  const reg = await navigator.serviceWorker.ready;
  return reg.pushManager.getSubscription();
}

// La clé VAPID voyage en base64url ; l'API navigateur veut des octets.
function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const normalized = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(normalized);
  // Buffer explicite : applicationServerKey n'accepte pas un
  // Uint8Array adossé à un SharedArrayBuffer (TS 5.7+).
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export async function subscribePush(vapidKey: string): Promise<void> {
  if (!pushSupported()) throw new Error('push_unsupported');
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') throw new Error('permission_denied');

  const reg = await navigator.serviceWorker.ready;
  const sub =
    (await reg.pushManager.getSubscription()) ??
    (await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidKey),
    }));

  const res = await apiClient.post('/notifications/subscriptions', sub.toJSON());
  if (res.status >= 400) throw new Error(`POST /notifications/subscriptions failed: ${res.status}`);
}

export async function unsubscribePush(): Promise<void> {
  const sub = await getSubscription();
  if (!sub) return;
  const { endpoint } = sub;
  await sub.unsubscribe();
  const res = await apiClient.delete('/notifications/subscriptions', { data: { endpoint } });
  if (res.status >= 400) throw new Error(`DELETE /notifications/subscriptions failed: ${res.status}`);
}
